<template>
    <div class="secure-sign__container">
        <v-container>
            <s-s-form-header
                title="Denzapine Monitoring Off-Label Treatment Agreement Form"
                document-number="PV01F12"
                version="4.0"
                effective-date="29 Feb 2024"
                review-date="29 Feb 2026"
                supersedes="3.0"
            ></s-s-form-header>
            <v-col class="text-center">
                <h1 class="mb-5">Denzapine Monitoring Off-Label Treatment Agreement Form</h1>
                <h4 class="mb-8">
                    Please complete all form fields before submission of application. Missing information may result in
                    delays to prescriber registration.
                </h4>
            </v-col>
            <v-col cols="12">
                <v-form ref="form" v-model="isFormValid" :disabled="isDisabled">
                    <v-row>
                        <v-col cols="12" sm="6" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::offLabelAgreementForm.patientFirstName"
                                :rules="validationRules.patientFirstName"
                                label="Patient First Name:"
                                mandatory
                            ></b-input>
                        </v-col>
                        <v-col cols="12" sm="6" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::offLabelAgreementForm.patientLastName"
                                :rules="validationRules.patientLastName"
                                label="Patient Last Name:"
                                mandatory
                            ></b-input>
                        </v-col>
                        <v-col cols="12" sm="6" class="py-0">
                            <b-date-picker-store
                                to-mutate="secure_sign_forms::offLabelAgreementForm.dateOfBirth"
                                label="Date of Birth:"
                                :rules="validationRules.dateOfBirth"
                                mandatory
                            ></b-date-picker-store>
                        </v-col>
                        <v-col cols="12" sm="6" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::offLabelAgreementForm.dms"
                                :rules="validationRules.dms"
                                label="DMS #:"
                                mandatory
                            ></b-input>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <p>For the use of Denzapine® (clozapine):</p>
                            <ul style="list-style-type: decimal">
                                <li>
                                    In a patient who does not meet the licensed indications or patient criteria as
                                    stated in the Summary of Product Characteristics (SmPC) or (Complete
                                    <strong>Section A</strong>)
                                </li>
                                <li>
                                    When Denzapine® needs to be prescribed for longer than the approved duration e.g.
                                    patient going on holiday (Complete <strong>Section B</strong>)
                                </li>
                                <li>
                                    Patient cannot attend their next routine blood test appointment (Complete
                                    <strong>Section C</strong>)
                                </li>
                            </ul>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Section A (Off-label Diagnosis)</h5>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <b-textarea
                                auto-grow
                                to-mutate="secure_sign_forms::offLabelAgreementForm.offLabelUseReasons"
                                :rules="validationRules.offLabelUseReasons"
                                label="Please state the reason(s) why the above-named patient meets the criteria for off-label use:"
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Section B (Travel Extension)</h5>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::offLabelAgreementForm.datesOfTravel"
                                :rules="validationRules.datesOfTravel"
                                label="Dates of travel:"
                            ></b-input>
                        </v-col>
                        <v-col cols="12" sm="12" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::offLabelAgreementForm.bloodTestBeforeHoliday"
                                label="Will they be having a blood test before their holiday?"
                                :rules="validationRules.bloodTestBeforeHoliday"
                                :radio-data="radioDataBinary"
                                connected-field="offLabelAgreementForm.bloodTestBeforeHolidayDate"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <b-date-picker-store
                                to-mutate="secure_sign_forms::offLabelAgreementForm.bloodTestBeforeHolidayDate"
                                label="If yes, please provide date:"
                                :rules="
                                    isBloodTestBeforeHolidayAdditionalFieldDisabled
                                        ? validationRules.bloodTestBeforeHolidayDate
                                        : []
                                "
                                :mandatory="isBloodTestBeforeHolidayAdditionalFieldDisabled"
                                :disabled="!isBloodTestBeforeHolidayAdditionalFieldDisabled"
                            ></b-date-picker-store>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <b-date-picker-store
                                to-mutate="secure_sign_forms::offLabelAgreementForm.bloodTestReturnDate"
                                label="What date will they have a blood test on their return?"
                                :rules="validationRules.bloodTestReturnDate"
                            ></b-date-picker-store>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::offLabelAgreementForm.additionalDaysInTotal"
                                :rules="validationRules.additionalDaysInTotal"
                                label="How many additional days in total do they need?"
                            ></b-input>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">
                                Section C (Patient cannot attend their next blood test appointment)
                            </h5>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::offLabelAgreementForm.reasonForNoAttendAtBloodTest"
                                :rules="validationRules.reasonForNoAttendAtBloodTest"
                                label="Reason for not being able to attend the next blood test appointment:"
                            ></b-input>
                        </v-col>
                        <v-col cols="12" sm="6" class="py-0">
                            <b-date-picker-store
                                to-mutate="secure_sign_forms::offLabelAgreementForm.nextBloodTestDate"
                                label="What will be the date of the next blood test?"
                                :rules="validationRules.nextBloodTestDate"
                            ></b-date-picker-store>
                        </v-col>
                        <v-col cols="12" sm="6" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::offLabelAgreementForm.offLicenceDays"
                                :rules="validationRules.offLicenceDays"
                                label="How many days do they need off-licence?"
                            ></b-input>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Summary Of Product Characteristics</h5>
                        </v-col>
                        <v-col cols="12">
                            <p>
                                The SmPC for Denzapine® states that ‘Denzapine® is indicated in treatment-resistant
                                schizophrenic patients and in schizophrenia patients who have severe, untreatable
                                neurological adverse reactions to other antipsychotic agents, including atypical
                                antipsychotics’. Treatment resistance is defined as a lack of satisfactory clinical
                                improvement despite the use of adequate doses of at least two different antipsychotic
                                agents, including an atypical antipsychotic agent, prescribed for adequate duration.
                                Denzapine® is also indicated in psychotic disorders occurring during the course of
                                Parkinson's disease, in cases where standard treatment has failed. The SmPC for
                                Denzapine® also describes those patients in whom Denzapine® is not recommended or is
                                contra-indicated (Please refer to the SmPC for further guidance).
                            </p>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Agreement</h5>
                        </v-col>
                        <v-col cols="12">
                            <p>
                                I, the undersigned, acknowledge and agree that Britannia Pharmaceuticals does not
                                recommend the use of Denzapine® in the circumstances applicable to the patient set out
                                below, and that the decision to treat is my decision and clinical responsibility alone.
                            </p>
                            <p>
                                I hereby waive any rights - I (or HOSPITAL NAME - please add Hospital Name in the
                                ‘Hospital Name’ section below (if applicable))) may have against Britannia
                                Pharmaceuticals, and release and discharge Britannia Pharmaceuticals from any claims I
                                may have against Britannia Pharmaceuticals, arising in connection with the use of
                                Denzapine® in the circumstances applicable to the patient referenced in this form.
                            </p>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col>
                            <h5 class="patient-form-title mt-5">Consultant Signature</h5>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="8" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::offLabelAgreementForm.printName"
                                :rules="validationRules.printName"
                                label="Print Name:"
                                mandatory
                            ></b-input>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <b-input
                                to-mutate="secure_sign_forms::offLabelAgreementForm.hospitalName"
                                label="Hospital Name:"
                            ></b-input>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-date-picker-store
                                hide-details
                                to-mutate="secure_sign_forms::offLabelAgreementForm.date"
                                label="Date"
                                :rules="validationRules.date"
                                mandatory
                            ></b-date-picker-store>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="8">
                            <secure-sign-form-signature-component
                                ref="signaturePad"
                                :form-type="form"
                                :is-disabled="isDisabled"
                            ></secure-sign-form-signature-component>
                        </v-col>

                        <v-col
                            v-if="!isDisabled"
                            cols="12"
                            sm="4"
                            class="d-flex flex-column justify-content-end align-items-center"
                        >
                            <secure-sign-form-recaptcha
                                ref="recaptcha"
                                :valid-recaptcha="validRecaptcha"
                                :is-disabled="isDisabled"
                                @expired="expired"
                                @verify="onRecaptchaVerify"
                            ></secure-sign-form-recaptcha>
                            <v-btn
                                :loading="loading"
                                class="mt-2"
                                max-width="304px"
                                width="100%"
                                size="x-large"
                                color="primary"
                                :disabled="isDisabled"
                                @click="submit"
                                >Submit
                            </v-btn>
                        </v-col>
                    </v-row>
                    <s-s-form-footer>
                        <p>
                            Should you have any queries, please do not hesitate to contact the Denzapine Monitoring
                            Service Team on 0333 200 4141
                        </p>
                        <p>Job Code: UK-Denz-2300035 Date of prep: Feb 2024</p>
                    </s-s-form-footer>
                </v-form>
            </v-col>
        </v-container>
    </div>
</template>

<script>
import SSFormHeader from './partials/SSFormHeader.vue';
import SSFormFooter from './partials/SSFormFooter.vue';

let formControl = require('./mixins/FormControl.js').default;
export default {
    components: { SSFormFooter, SSFormHeader },
    mixins: [formControl],
    data() {
        return {
            validationRules: {
                patientFirstName: [(v) => !!v || t('users::validation.field.required')],
                patientLastName: [(v) => !!v || t('users::validation.field.required')],
                dateOfBirth: [(v) => !!v || t('users::validation.field.required')],
                dms: [(v) => !!v || t('users::validation.field.required')],
                // offLabelUseReasons: [(v) => !!v || t('users::validation.field.required')],
                // datesOfTravel: [(v) => !!v || t('users::validation.field.required')],
                // bloodTestBeforeHoliday: [(v) => !!v || t('users::validation.field.required')],
                bloodTestBeforeHolidayDate: [(v) => !!v || t('users::validation.field.required')],
                // bloodTestReturnDate: [(v) => !!v || t('users::validation.field.required')],
                // additionalDaysInTotal: [(v) => !!v || t('users::validation.field.required')],
                // reasonForNoAttendAtBloodTest: [(v) => !!v || t('users::validation.field.required')],
                // nextBloodTestDate: [(v) => !!v || t('users::validation.field.required')],
                // offLicenceDays: [(v) => !!v || t('users::validation.field.required')],
                printName: [(v) => !!v || t('users::validation.field.required')],
                signature: [(v) => !!v || t('users::validation.field.required')],
                date: [(v) => !!v || t('users::validation.field.required')],
            },
            radioDataBinary: [
                { label: 'Yes', value: 'Yes' },
                { label: 'No', value: 'No' },
            ],
            isFormValid: false,
            formType: 'off-label-agreement-form',
            form: 'offLabelAgreementForm',
        };
    },
    computed: {
        isBloodTestBeforeHolidayAdditionalFieldDisabled: {
            get() {
                return this.$store.state.secure_sign_forms.offLabelAgreementForm.bloodTestBeforeHoliday === 'Yes';
            },
        },
    },
    created() {
        this.getFormData();
    },
};
</script>

<style scoped></style>
